import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { useTranslation } from "react-i18next";
import { AllDataBreeding } from "../../../../api/breeding";
import { DataTableColumn, DataTableRow } from "../../../common/DataTable";
import { EntityCard } from "../../../common/EntityCard";
import { FavoriteButton, FavoriteHeader } from "../../../common/FavoriteButton";

export type ClientBreedingColumnKey =
  | "favorite"
  | "name"
  | "company"
  | "city"
  | "buildings"
  | "devices"
  | "open";

/** Les colonnes triables sont celles qui ont une valeur de tri. */
export const CLIENT_BREEDING_SORT_VALUES: Partial<
  Record<ClientBreedingColumnKey, (entry: AllDataBreeding) => string>
> = {
  name: (entry) => entry.breeding.name,
  company: (entry) => entry.company.company_name,
  city: (entry) => entry.breeding.city,
  buildings: (entry) => String(entry.buildings.length),
  devices: (entry) => String(entry.devices.length),
};

export const useClientBreedingColumns =
  (): DataTableColumn<ClientBreedingColumnKey>[] => {
    const { t } = useTranslation();
    const columns: DataTableColumn<ClientBreedingColumnKey>[] = [
      {
        key: "favorite",
        header: <FavoriteHeader />,
        className: "w-8 flex items-center justify-center",
      },
      { key: "name", header: t("section-breeding"), className: "w-3/12" },
      { key: "company", header: t("section-company"), className: "w-3/12" },
      { key: "city", header: t("company-city"), className: "w-2/12" },
      {
        key: "buildings",
        header: t("breeding-client-buildings"),
        className: "w-1/12",
      },
      {
        key: "devices",
        header: t("section-devices"),
        className: "w-1/12",
      },
      { key: "open", header: "", className: "w-1/12 text-right" },
    ];
    return columns.map((column) => ({
      ...column,
      sortable: column.key in CLIENT_BREEDING_SORT_VALUES,
    }));
  };

type ClientBreedingRowProps = {
  entry: AllDataBreeding;
  isFavorite: boolean;
  onToggleFavorite: (e: React.MouseEvent) => void;
  onOpen: () => void;
};

const cityOf = (entry: AllDataBreeding) =>
  [entry.breeding.zip_code, entry.breeding.city].filter(Boolean).join(" ") ||
  "-";

export const ClientBreedingRow = ({
  entry,
  isFavorite,
  onToggleFavorite,
  onOpen,
}: ClientBreedingRowProps) => {
  const columns = useClientBreedingColumns();

  return (
    <DataTableRow
      columns={columns}
      onClick={onOpen}
      cells={{
        favorite: (
          <FavoriteButton isFavorite={isFavorite} onToggle={onToggleFavorite} />
        ),
        name: entry.breeding.name || "-",
        company: entry.company.company_name || "-",
        city: cityOf(entry),
        buildings: entry.buildings.length,
        devices: entry.devices.length,
        open: (
          <FontAwesomeIcon
            icon="chevron-right"
            aria-hidden="true"
            className="text-grey-readable"
          />
        ),
      }}
    />
  );
};

export const ClientBreedingMobileCard = ({
  entry,
  isFavorite,
  onToggleFavorite,
  onOpen,
}: ClientBreedingRowProps) => {
  const { t } = useTranslation();

  return (
    <EntityCard
      title={entry.breeding.name || "-"}
      isFavorite={isFavorite}
      onToggleFavorite={onToggleFavorite}
      onClick={onOpen}
      rows={[
        {
          label: t("section-company"),
          value: entry.company.company_name || "-",
        },
        { label: t("company-city"), value: cityOf(entry) },
        {
          label: t("breeding-client-buildings"),
          value: entry.buildings.length,
        },
        { label: t("section-devices"), value: entry.devices.length },
      ]}
    />
  );
};
